import React, { useState, useEffect } from 'react';
import { BringzzText } from "@bringzz/components";
import useRequest from '../hooks/useRequest';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { ORDER_STATUS } from '../utils/constants';

const RecentOrders = () => {
    const { sendRequest, loading } = useRequest();
    const { user } = useAuth();
    const { navigate } = useNavigation();
    const [recentOrders, setRecentOrders] = useState([]);

    const parseDate = (dateString) => {
        // Convert "29.09.2025 13:13" to JavaScript Date
        const [datePart, timePart] = dateString.split(' ');
        const [day, month, year] = datePart.split('.');
        const [hours, minutes] = timePart.split(':');
        return new Date(year, month - 1, day, hours, minutes);
    };

    useEffect(() => {
        const fetchOrders = async () => {
            if (user && user.id) {
                try {
                    const response = await sendRequest({
                        method: 'GET',
                        url: `/module/order/getAllOrders`,
                    });

                    if (response?.status === 'success') {
                        // Process all orders and take the 10 most recent
                        const allOrders = response.data.map(order => ({
                            ...order,
                            orderdate: parseDate(order.created),
                            // Simplify status to completed or ready
                            displayStatus: order.deliveryStatus === ORDER_STATUS.DONE ? 'completed' : 'ready'
                        }));

                        // Sort by date (newest first) and take first 10
                        const sortedOrders = allOrders
                            .sort((a, b) => b.orderdate - a.orderdate)
                            .slice(0, 10);

                        setRecentOrders(sortedOrders);
                    }
                } catch (error) {
                    console.error('Error fetching orders:', error);
                }
            }
        };

        fetchOrders();
    }, [user?.id]);

    const getCustomerInitials = (name) => {
        if (!name) return 'U';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    const formatPrice = (price) => {
        if (typeof price === 'number') {
            return price.toFixed(2);
        }
        return price || '0.00';
    };

    if (loading) {
        return (
            <div className="px-4 py-6 bg-white">
                <BringzzText tag="h3" className="text-lg font-semibold mb-4">
                    Recent Orders
                </BringzzText>
                <div className="flex space-x-4 overflow-x-auto pb-2">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex-shrink-0">
                            <div className="w-20 h-20 rounded-full bg-gray-200 animate-pulse mb-2"></div>
                            <div className="w-16 h-4 bg-gray-200 animate-pulse rounded mx-auto mb-1"></div>
                            <div className="w-12 h-3 bg-gray-200 animate-pulse rounded mx-auto"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (recentOrders.length === 0) {
        return (
            <div className="px-4 bg-white">
                <BringzzText tag="h3" className="text-lg font-semibold mb-4">
                    Recent Orders
                </BringzzText>
                <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                            </svg>
                        </div>
                        <BringzzText className="text-gray-500 text-sm">
                            No orders yet
                        </BringzzText>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 bg-white">
            <div className="mb-4">
                <BringzzText tag="h3" className="text-lg font-semibold">
                    Recent Orders
                </BringzzText>
                <BringzzText tag="p" className="text-xs text-gray-500">
                    Your latest {recentOrders.length} orders
                </BringzzText>
            </div>

            <div className="flex space-x-4 overflow-x-auto pb-3 scrollbar-hide">
                {recentOrders.map((order, index) => {
                    const isCompleted = order.displayStatus === 'completed';

                    return (
                        <div key={order.integrationId || index} className="flex-shrink-0">
                            <div className="relative group cursor-pointer" onClick={() => navigate(`/settings/pharmacy/orders/view/${order.integrationId}`)}>
                                {/* Story circle with status ring */}
                                <div className={`relative w-20 h-20 rounded-full p-1 transition-transform hover:scale-105 ${
                                    isCompleted
                                        ? 'bg-gradient-to-tr from-magic-lilac to-purple-500'
                                        : 'bg-gradient-to-tr from-amber-400 to-orange-500'
                                }`}>
                                    <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                                        <div className={`w-[68px] h-[68px] rounded-full flex flex-col items-center justify-center ${
                                            isCompleted ? 'bg-magic-lilac' : 'bg-amber-500'
                                        }`}>
                                            <BringzzText className="text-lg font-bold text-white">
                                                {getCustomerInitials(order.customerName)}
                                            </BringzzText>
                                            <BringzzText className="text-xs font-medium text-white">
                                                €{formatPrice(order.price)}
                                            </BringzzText>
                                        </div>
                                    </div>
                                </div>

                                {/* Status badge */}
                                <div className={`absolute -bottom-1 left-1/2 transform -translate-x-1/2 px-2 py-0.5 rounded-full text-[10px] font-medium text-white ${
                                    isCompleted ? 'bg-magic-lilac' : 'bg-amber-500'
                                }`}>
                                    {isCompleted ? 'Completed' : 'Ready'}
                                </div>
                            </div>

                            {/* Customer name */}
                            <div className="mt-3 text-center">
                                <BringzzText className="text-xs text-gray-700 font-medium truncate max-w-[80px] mx-auto">
                                    {order.customerName?.split(' ')[0] || 'Customer'}
                                </BringzzText>
                                <BringzzText className="text-[10px] text-gray-500">
                                    {order.products?.length || 0} items
                                </BringzzText>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RecentOrders;